// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import { gql, useMutation, useReactiveVar } from "@apollo/client";
import { cilChevronLeft, cilLockLocked, cilUser } from "@coreui/icons-pro";
import CIcon from "@coreui/icons-react";
import {
  CAlert,
  CButton,
  CCol,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
} from "@coreui/react-pro";
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { setAuthToken } from "../../auth";
import { AUTH_STATE, authError, authState, clearReturnTo } from "../../graphql/policies/auth";

export const LOGIN_MUTATION = gql`
  mutation LoginMutation($email: String!, $password: String!) {
    LogIn(email: $email, password: $password) {
      jwt
    }
  }
`;

const Email = () => {
  const navigate = useNavigate();
  // Why the last session ended, if it ended involuntarily. Reading the
  // message directly beats inferring it from an auth state: a state
  // says where the machine is, not what to tell the user.
  const signOutReason = useReactiveVar(authError);
  const [formState, setFormState] = useState({ email: "", password: "" });
  const [loginMessage, setLoginMessage] = useState(null);

  // Login resolves the return target once (reactive var first, router
  // state as a fallback) and hands it down through the Outlet. Reading
  // `location.state` directly here was a second, incompatible
  // derivation that ignored a captured target.
  const { from = "/" } = useOutletContext() ?? {};

  const onSuccess = () => {
    // Navigate first, then consume: clearing mutates a reactive var
    // that re-renders Login and resets the `from` handed down through
    // the Outlet.
    navigate(from, { replace: true });
    clearReturnTo();
  };

  useEffect(() => {
    if (signOutReason) {
      setLoginMessage(signOutReason);
    }
  }, [signOutReason]);

  const [login, { loading }] = useMutation(LOGIN_MUTATION, {
    variables: {
      email: formState.email,
      password: formState.password,
    },
    onCompleted: ({ LogIn: { jwt = "" } = {} }) => {
      if (jwt && jwt.length > 0) {
        setAuthToken(jwt);
        authError("");
        authState(AUTH_STATE.LOGGED_IN);
        onSuccess();
      }
    },
    onError: (error) => {
      const code = error?.graphQLErrors?.[0]?.extensions?.code;
      if (code === "E_ACCOUNT_MIGRATED") {
        // Server has refused the legacy pathway because this account has
        // already been adopted into Logto (logto_sub is set). Tell the
        // user to switch tabs rather than letting them retry the form.
        setLoginMessage(
          'This account has been migrated to single sign-on. Please use the "Logto" tab to sign in.',
        );
      } else if (code === "E_AUTH_FAILED" || error.message === "AUTH_FAILED") {
        setLoginMessage("Authorisation failed: email or password not accepted.");
      } else {
        setLoginMessage("There was an error signing in. Please contact support.");
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  return (
    <CForm
      onSubmit={(e) => {
        e.preventDefault();
        setLoginMessage(null);
        login();
      }}
    >
      <p className={"mb-3"}>
        <Link to={".."}>
          <CIcon icon={cilChevronLeft} /> Back to sign in with LogTo
        </Link>
      </p>
      <p className="text-medium-emphasis">Sign in to your account</p>
      {loginMessage && (
        <CAlert color="danger" role="alert" data-testid="login-error">
          {loginMessage}
        </CAlert>
      )}
      <CInputGroup className="mb-3">
        <CInputGroupText>
          <CIcon icon={cilUser} />
        </CInputGroupText>
        <CFormInput
          placeholder="Email"
          autoComplete="email"
          data-testid="login-email"
          value={formState.email}
          onChange={(e) => setFormState({ ...formState, email: e.target.value })}
        />
      </CInputGroup>
      <CInputGroup className="mb-4">
        <CInputGroupText>
          <CIcon icon={cilLockLocked} />
        </CInputGroupText>
        <CFormInput
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          data-testid="login-password"
          value={formState.password}
          onChange={(e) => setFormState({ ...formState, password: e.target.value })}
        />
      </CInputGroup>
      <CRow>
        <CCol xs={6}>
          <CButton
            color="primary"
            className="px-4"
            type="submit"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </CButton>
        </CCol>
      </CRow>
    </CForm>
  );
};

export default Email;
